import { useCallback, useEffect, useState } from "react";
import { getJlptLevel, setJlptLevel } from "../utils/jlptLevelStorage";
import { getCloudProgress, putCloudProgress } from "../utils/progressCloud";
import { useAuth } from "../context/AuthContext";
import { ProgressSyncStatus } from "./progressTypes";

const FEATURE_KEY = "jlptLevel";

// The user's self-reported JLPT level, set on the profile page. Pages that
// filter by level (grammar, kanji quiz, kanji writing practice) read this to
// pick their initial filter, so studying stays consistent across the site.
export function useJlptLevel() {
    const { status, user } = useAuth();
    const [level, setLevelState] = useState<string | null>(() => getJlptLevel());
    const [syncStatus, setSyncStatus] = useState<ProgressSyncStatus>("local");

    useEffect(() => {
        if (status !== "authenticated" || !user?.identityId) {
            setSyncStatus("local");
            return undefined;
        }

        let cancelled = false;
        setSyncStatus("loading");

        getCloudProgress(user.identityId, FEATURE_KEY)
            .then((remote) => {
                if (cancelled) return;
                if (remote) {
                    const remoteLevel = remote.data ?? null;
                    setLevelState(remoteLevel);
                    setJlptLevel(remoteLevel);
                }
                setSyncStatus("ready");
            })
            .catch(() => {
                if (!cancelled) setSyncStatus("error");
            });

        return () => {
            cancelled = true;
        };
    }, [status, user?.identityId]);

    const setLevel = useCallback(
        (nextLevel: string | null) => {
            setLevelState(nextLevel);
            setJlptLevel(nextLevel);

            if (status === "authenticated" && user?.identityId) {
                putCloudProgress(user.identityId, FEATURE_KEY, nextLevel).catch(() => {
                    // Best-effort: the preference still applies locally for this session.
                });
            }
        },
        [status, user?.identityId]
    );

    return { level, setLevel, syncStatus };
}
